import logo from "../../assets/logo.png";
import { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../context";
import styles from "./styles.module.css";
import { adminLinks, userLinks } from "./links";
import {
  FaSignOutAlt,
  FaUser,
  FaBars,
  FaTimes,
  FaCaretDown,
} from "react-icons/fa";
import { RiCustomerService2Fill } from "react-icons/ri";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { IconType } from "react-icons";

type NavItem = {
  route: string;
  name: string;
  Icon: IconType;
  allowed: string[];
  children?: NavItem[];
  className?: string;
};

type NavProps = {
  pageTitle: string;
};

const Navigation = ({ pageTitle }: NavProps) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {},
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const role = user?.role || "";
  const navLinks: NavItem[] = role === "Admin" ? adminLinks : userLinks;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const visibleLinks = useMemo(() => {
    return navLinks.filter((item) => item.allowed?.includes(role));
  }, [navLinks, role]);

  const logoutHandler = () => {
    logout();
    navigate("/");
  };

  const toggleHandler = (key: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isActiveParent = (item: NavItem) => {
    if (pageTitle === item.name) return true;
    if (item.route && location.pathname === item.route) return true;

    return item.children?.some((child) => location.pathname === child.route);
  };

  const renderLinks = (links: NavItem[]) => {
    return links.map((item) => {
      const Icon = item.Icon;
      const hasChildren = Boolean(item.children?.length);
      const isOpen = expandedItems[item.name];
      const isParentActive = isActiveParent(item);

      if (hasChildren) {
        return (
          <li key={item.name} className={styles.navGroup}>
            <button
              type="button"
              className={`${styles.navButton} ${
                isParentActive ? styles.active : ""
              }`}
              onClick={() => toggleHandler(item.name)}
              aria-expanded={Boolean(isOpen)}
            >
              <span className={styles.navIcon}>
                <Icon />
              </span>

              <span className={styles.navText}>{item.name}</span>

              <FaCaretDown
                className={`${styles.chevron} ${isOpen ? styles.rotate : ""}`}
              />
            </button>

            {isOpen ? (
              <ul className={styles.childList}>
                {item.children?.map((child) => {
                  const ChildIcon = child.Icon;

                  return (
                    <li key={child.name}>
                      <NavLink
                        to={child.route}
                        className={({ isActive }) =>
                          `${styles.childLink} ${
                            isActive ? styles.childActive : ""
                          }`
                        }
                      >
                        <ChildIcon className={styles.childIcon} />
                        <span>{child.name}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </li>
        );
      }

      return (
        <li key={item.name}>
          <NavLink
            to={item.route}
            className={({ isActive }) =>
              `${styles.navLink} ${
                isActive || pageTitle === item.name ? styles.active : ""
              }`
            }
          >
            <span className={styles.navIcon}>
              <Icon />
            </span>

            <span className={styles.navText}>{item.name}</span>
          </NavLink>
        </li>
      );
    });
  };

  return (
    <>
      <button
        type="button"
        className={styles.mobileToggle}
        onClick={() => setMobileOpen((prev) => !prev)}
        aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
        aria-expanded={mobileOpen}
      >
        {mobileOpen ? <FaTimes /> : <FaBars />}
      </button>

      {mobileOpen ? (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <aside className={`${styles.sidebar} ${mobileOpen ? styles.open : ""}`}>
        <div className={styles.sidebarInner}>
          <div className={styles.brandArea}>
            <Link to="/" className={styles.logoLink} aria-label="Triad Africa">
              <img src={logo} alt="Triad Africa" className={styles.logo} />
            </Link>

            <div className={styles.userCard}>
              <div className={styles.avatar}>
                {(user?.firstName || user?.name || user?.email || "U")
                  ?.charAt(0)
                  ?.toUpperCase()}
              </div>

              <div className={styles.userInfo}>
                <strong>
                  {user?.firstName || user?.fullname || "Welcome"}
                </strong>
                <span>{role || "User"}</span>
              </div>
            </div>
          </div>

          <nav className={styles.navArea} aria-label="Dashboard navigation">
            <span className={styles.navLabel}>Menu</span>

            <ul className={styles.navList}>{renderLinks(visibleLinks)}</ul>

            {user?.role !== "Admin" ? (
              <>
                <span className={styles.navLabel}>Support</span>

                <ul className={styles.navList}>
                  <li>
                    <NavLink
                      to="/app/profile"
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.active : ""}`
                      }
                    >
                      <span className={styles.navIcon}>
                        <FaUser />
                      </span>
                      <span className={styles.navText}>Account</span>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/app/contact"
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.active : ""}`
                      }
                    >
                      <span className={styles.navIcon}>
                        <RiCustomerService2Fill />
                      </span>
                      <span className={styles.navText}>Contact Us</span>
                    </NavLink>
                  </li>
                </ul>
              </>
            ) : null}
          </nav>

          <div className={styles.logoutArea}>
            <button
              type="button"
              className={styles.logoutButton}
              onClick={logoutHandler}
            >
              <span className={styles.navIcon}>
                <FaSignOutAlt />
              </span>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Navigation;
